import { useState, useEffect, useMemo, useCallback, type CSSProperties } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Skeleton from "@mui/material/Skeleton";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import {
  AccessTime,
  ArrowBack,
  CalendarMonth,
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ContentCut,
  Map,
  Person,
  Phone,
} from "@mui/icons-material";
import { apiRequest, fetchPublicBookingConfig, publicBookingRequest } from "@/lib/queryClient";
import {
  resolvePublicSlug,
  apiPublicPath,
  parseJsonBytesField,
  mapServiceResponse,
  mapStaffResponse,
  slotsToUi,
  localDateTimeToIso,
  resolveDepositCents,
  unwrapPagedList,
  coerceBookingApiEntityId,
  type BookingApiService,
  type BookingApiSlot,
  type BookingApiStaff,
} from "@/lib/bookingApi";
import { useToast } from "@/hooks/useToast";
import { validateRequiredName, validateEmailRequired } from "@/lib/formValidation";
import { isReservedSlug, normalizeSlugInput } from "@/lib/slugReservedWords";
import { formatCents, formatDuration, getInitials, formatDateOnly } from "@/lib/utils";
import ThemeToggle from "@/components/ThemeToggle";
import type { Business, Service, Staff, BookingPageConfig } from "@shared/schema";
import { radiusToCss } from "@shared/schema";
import { useTranslation } from "react-i18next";
import { Link, useParams, useNavigate } from "react-router-dom";

type BookingStep = "services" | "staff" | "datetime" | "details" | "confirmation";

interface AvailableSlot {
  time: string;
  display: string;
}

const WEEKDAY_ORDER_MON_FIRST = [1, 2, 3, 4, 5, 6, 0] as const;

function startOfDayLocal(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** ISO date strings per cell; `null` = padding (month grid, Monday-first). */
function buildMonthCalendarCells(year: number, monthIndex: number): (string | null)[] {
  const first = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  const mondayFirstOffset = (first.getDay() + 6) % 7;
  const cells: (string | null)[] = [];
  for (let i = 0; i < mondayFirstOffset; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) {
    const iso = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push(iso);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function BookingPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState<BookingStep>("services");
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [customerDetails, setCustomerDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [detailsFieldErrors, setDetailsFieldErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
  }>({});
  const [bookingConfirmed, setBookingConfirmed] = useState<Record<string, unknown> | null>(null);
  const [slugDraft, setSlugDraft] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });

  const isPreview =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1";

  const { slug: slugFromPath } = useParams<{ slug?: string }>();
  /** `/book/:slug` wins; else `REACT_APP_BOOKING_PUBLIC_SLUG`; otherwise slug entry on `/` (see `resolvePublicSlug`). */
  const slug = resolvePublicSlug(slugFromPath);

  const { data: rawConfig, isLoading: loadingBusiness } = useQuery({
    queryKey: ["public-config", slug, isPreview],
    queryFn: () => fetchPublicBookingConfig(slug!, { preview: isPreview }),
    enabled: !!slug,
  });

  const cfg = useMemo((): BookingPageConfig | null | undefined => {
    if (!rawConfig) return undefined;
    const draft = parseJsonBytesField<BookingPageConfig>(rawConfig.bookingPageDraftConfig);
    const live = parseJsonBytesField<BookingPageConfig>(rawConfig.bookingPageConfig);
    if (isPreview) return draft ?? live ?? null;
    return live ?? null;
  }, [rawConfig, isPreview]);

  const business = useMemo((): Business | undefined => {
    if (!rawConfig) return undefined;
    const br = rawConfig;
    return {
      id: br.companyId,
      name: br.name,
      slug: br.slug,
      timezone: br.timezone,
      currency: br.currency,
      category: "beauty",
      address: null,
      phone: null,
      email: null,
      website: null,
      description: null,
      logoUrl: null,
      coverImageUrl: null,
      accentColor: "#7c3aed",
      minLeadTimeMins: br.minLeadTimeMins,
      maxBookingDays: br.maxBookingHorizonDays,
      slotIncrementMins: br.slotIncrementMins,
      cancellationPolicyText: br.cancellationPolicyText ?? null,
      depositEnabled: br.defaultDepositEnabled,
      defaultDepositType: br.defaultDepositType,
      defaultDepositValue: br.defaultDepositValue,
      bookingPageConfig: null,
      bookingPageDraftConfig: null,
      brandingUpdatedAt: null,
    } as Business;
  }, [rawConfig]);

  const { data: services, isLoading: loadingServices, isError: servicesError, refetch: refetchServices } = useQuery<
    Service[]
  >({
    queryKey: ["public-services", slug],
    queryFn: async () => {
      const res = await publicBookingRequest("GET", apiPublicPath(slug!, "/services"));
      const arr = unwrapPagedList<BookingApiService>(await res.json());
      return arr.map(mapServiceResponse);
    },
    enabled: !!slug,
  });

  const { data: eligibleStaff } = useQuery<Staff[]>({
    queryKey: ["public-staff", slug, selectedService?.id],
    queryFn: async () => {
      const base = apiPublicPath(slug!, "/staff");
      const path = selectedService
        ? `${base}?serviceId=${encodeURIComponent(String(selectedService.id))}`
        : base;
      const res = await publicBookingRequest("GET", path);
      const arr = unwrapPagedList<BookingApiStaff>(await res.json());
      return arr.map(mapStaffResponse);
    },
    enabled: !!slug && !!selectedService,
  });

  const { data: availableSlots } = useQuery<AvailableSlot[]>({
    queryKey: ["public-availability", slug, selectedService?.id, selectedStaff?.id, selectedDate],
    queryFn: async () => {
      const params = new URLSearchParams({
        serviceId: String(selectedService!.id),
        date: selectedDate,
      });
      if (selectedStaff?.id) params.set("staffId", String(selectedStaff.id));
      const res = await publicBookingRequest("GET", `${apiPublicPath(slug!, "/availability")}?${params}`);
      const raw = unwrapPagedList<BookingApiSlot>(await res.json());
      return slotsToUi(raw);
    },
    enabled: !!slug && !!selectedService && !!selectedDate,
  });

  const depositInfo = useMemo(() => {
    if (!rawConfig || !selectedService) return null;
    const cents = resolveDepositCents(rawConfig, selectedService);
    return {
      enabled: cents > 0,
      depositAmountCents: cents,
      priceCents: selectedService.priceCents,
      currency: selectedService.currency || rawConfig.currency,
    };
  }, [rawConfig, selectedService]);

  useEffect(() => {
    if (!cfg) return;
    if (cfg.themeMode === "dark") {
      document.documentElement.classList.add("dark");
    } else if (cfg.themeMode === "light") {
      document.documentElement.classList.remove("dark");
    } else if (cfg.themeMode === "auto") {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      if (prefersDark) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    }
  }, [cfg?.themeMode]);

  const todayStart = useMemo(() => startOfDayLocal(new Date()), []);
  const lastBookableDay = useMemo(() => {
    const n = new Date(todayStart);
    n.setDate(n.getDate() + (business?.maxBookingDays ?? 90));
    return startOfDayLocal(n);
  }, [todayStart, business?.maxBookingDays]);

  const monthCalendarCells = useMemo(
    () => buildMonthCalendarCells(calendarMonth.getFullYear(), calendarMonth.getMonth()),
    [calendarMonth],
  );

  const firstOfCalendarMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
  const firstOfTodayMonth = new Date(todayStart.getFullYear(), todayStart.getMonth(), 1);
  const canGoPrevMonth = firstOfCalendarMonth.getTime() > firstOfTodayMonth.getTime();
  const canGoNextMonth = useMemo(() => {
    const firstNext = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1);
    return startOfDayLocal(firstNext) <= lastBookableDay;
  }, [calendarMonth, lastBookableDay]);

  useEffect(() => {
    if (step !== "datetime" || !selectedDate) return;
    const parts = selectedDate.split("-").map(Number);
    if (parts.length !== 3) return;
    const [y, m, d] = parts;
    if (!y || !m || !d) return;
    setCalendarMonth(new Date(y, m - 1, 1));
  }, [step, selectedDate]);

  const bookingMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", apiPublicPath(slug!, "/bookings"), data).then((r) => r.json()),
    onSuccess: (data: { checkoutUrl?: string }) => {
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        setBookingConfirmed(data as Record<string, unknown>);
        setStep("confirmation");
      }
    },
    onError: (err: Error) =>
      toast({ title: t("toasts.bookingFailed"), description: err.message, variant: "destructive" }),
  });

  function selectService(service: Service) {
    setSelectedService(service);
    setSelectedStaff(null);
    setSelectedTime("");
    if (cfg?.showStaff === false) {
      setSelectedStaff(null);
      setStep("datetime");
    } else {
      setStep("staff");
    }
  }

  function selectStaff(member: Staff | null) {
    setSelectedStaff(member);
    setSelectedTime("");
    setStep("datetime");
  }

  function selectSlot(slot: AvailableSlot) {
    setSelectedTime(slot.time);
    setStep("details");
  }

  function goBack() {
    if (step === "staff") setStep("services");
    else if (step === "datetime") {
      if (cfg?.showStaff === false) setStep("services");
      else setStep("staff");
    } else if (step === "details") setStep("datetime");
  }

  function submitBooking() {
    if (!slug) {
      toast({ title: t("toasts.bookingFailed"), description: "Missing public slug", variant: "destructive" });
      return;
    }
    const nextErr: { firstName?: string; lastName?: string; email?: string } = {};
    const fnErr = validateRequiredName(customerDetails.firstName, t("newBookingDrawer.firstName"));
    if (fnErr) nextErr.firstName = fnErr;
    const lnErr = validateRequiredName(customerDetails.lastName, t("newBookingDrawer.lastName"));
    if (lnErr) nextErr.lastName = lnErr;
    const emErr = validateEmailRequired(customerDetails.email);
    if (emErr) nextErr.email = emErr;
    setDetailsFieldErrors(nextErr);
    if (Object.keys(nextErr).length > 0) return;

    if (!selectedService || !selectedDate || !selectedTime) {
      toast({ title: t("toasts.fillRequiredFields"), variant: "destructive" });
      return;
    }
    bookingMutation.mutate({
      customerEmail: customerDetails.email,
      customerName: `${customerDetails.firstName} ${customerDetails.lastName}`.trim(),
      customerPhone: customerDetails.phone || undefined,
      serviceId: coerceBookingApiEntityId(selectedService.id),
      staffId: selectedStaff ? coerceBookingApiEntityId(selectedStaff.id) : undefined,
      startAt: localDateTimeToIso(selectedDate, selectedTime),
    });
  }

  const activeServices = useMemo(() => services?.filter((s) => s.active) ?? [], [services]);

  const grouped = useMemo(
    () =>
      activeServices.reduce<Record<string, Service[]>>((acc, s) => {
        const cat = s.category || "General";
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(s);
        return acc;
      }, {}),
    [activeServices],
  );

  const todayIsoStr = useMemo(
    () =>
      `${todayStart.getFullYear()}-${String(todayStart.getMonth() + 1).padStart(2, "0")}-${String(todayStart.getDate()).padStart(2, "0")}`,
    [todayStart],
  );

  const isDateSelectable = useCallback(
    (iso: string) => {
      const d = startOfDayLocal(new Date(`${iso}T12:00:00`));
      return d.getTime() >= todayStart.getTime() && d.getTime() <= lastBookableDay.getTime();
    },
    [todayStart, lastBookableDay],
  );

  const calendarMonthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language?.replace(/_/g, "-") || "en-GB", {
        month: "long",
        year: "numeric",
      }).format(calendarMonth),
    [calendarMonth, i18n.language],
  );

  const showPrices = cfg?.showPrices ?? true;
  const showDurations = cfg?.showDurations ?? true;
  const showAddress = cfg?.showAddress ?? true;
  const showPhone = cfg?.showPhone ?? true;
  /** Custom title from booking-page branding, or "Book with {{name}}" from business settings / API. */
  const bookingPageHeading = useMemo(() => {
    const custom = cfg?.pageTitle?.trim();
    if (custom) return custom;
    const name = business?.name?.trim();
    if (name) return t("bookingFlow.bookWithName", { name });
    return "";
  }, [cfg?.pageTitle, business?.name, t]);
  const tagline = cfg?.tagline || "";
  const logoUrl = cfg?.logoUrl || "";
  const coverImageUrl = cfg?.coverImageUrl || "";
  const galleryUrls = (cfg?.galleryUrls || []).filter(Boolean).slice(0, 6);
  const poweredBy = cfg?.poweredByEposNow ?? true;

  const themeStyle: CSSProperties = cfg
    ? ({
        "--brand-primary": cfg.primaryColor,
        "--brand-radius": radiusToCss(cfg.radius),
      } as CSSProperties)
    : {};

  const primaryColor = cfg?.primaryColor || theme.palette.primary.main;

  const visibleSteps =
    cfg?.showStaff === false
      ? (["services", "datetime", "details"] as BookingStep[])
      : (["services", "staff", "datetime", "details"] as BookingStep[]);
  const visibleLabels = useMemo(
    () =>
      cfg?.showStaff === false
        ? [t("bookingFlow.stepService"), t("bookingFlow.stepDateTime"), t("bookingFlow.stepYourDetails")]
        : [
            t("bookingFlow.stepService"),
            t("bookingFlow.stepStaff"),
            t("bookingFlow.stepDateTime"),
            t("bookingFlow.stepYourDetails"),
          ],
    [cfg?.showStaff, t],
  );

  const publicSlugReservedError = useMemo(() => {
    const cleaned = normalizeSlugInput(slugDraft);
    if (!cleaned) return "";
    return isReservedSlug(cleaned) ? t("settings.business.slugReserved") : "";
  }, [slugDraft, t]);

  function handlePublicSlugContinue() {
    const cleaned = normalizeSlugInput(slugDraft);
    if (!cleaned) {
      toast({ title: t("bookingFlow.slugRequired"), variant: "destructive" });
      return;
    }
    if (isReservedSlug(cleaned)) {
      toast({ title: t("settings.business.slugReserved"), variant: "destructive" });
      return;
    }
    navigate(`/book/${encodeURIComponent(cleaned)}`);
  }

  if (!slug) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Paper elevation={2} sx={{ p: 4, maxWidth: 440, width: "100%" }}>
          <Stack spacing={2}>
            <Typography variant="h6" component="h1">
              {t("bookingFlow.slugPromptTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("bookingFlow.slugPromptSubtitle")}
            </Typography>
            <TextField
              label={t("settings.business.urlSlug")}
              placeholder={t("bookingFlow.slugPlaceholder")}
              value={slugDraft}
              onChange={(e) => setSlugDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handlePublicSlugContinue();
                }
              }}
              fullWidth
              autoFocus
              autoComplete="off"
              error={Boolean(publicSlugReservedError)}
              helperText={publicSlugReservedError || undefined}
              inputProps={{ "data-testid": "input-public-slug" }}
            />
            <Button
              variant="contained"
              onClick={handlePublicSlugContinue}
              fullWidth
              disabled={Boolean(publicSlugReservedError)}
              data-testid="button-public-slug-continue"
            >
              {t("bookingFlow.continue")}
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (loadingBusiness) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Skeleton variant="rounded" width={192} height={48} />
      </Box>
    );
  }

  const headerContent = (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      {logoUrl ? (
        <Box
          component="img"
          src={logoUrl}
          alt={String(t("bookingFlow.logoAlt"))}
          sx={{ width: 32, height: 32, borderRadius: 1, objectFit: "cover" }}
          data-testid="booking-logo"
        />
      ) : (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: primaryColor,
          }}
        >
          <ContentCut sx={{ fontSize: 18, color: "common.white" }} />
        </Box>
      )}
      <Box>
        <Typography fontWeight={600} data-testid="booking-title">
          {bookingPageHeading}
        </Typography>
        {tagline && (
          <Typography variant="caption" color="text.secondary" data-testid="booking-tagline">
            {tagline}
          </Typography>
        )}
      </Box>
    </Stack>
  );

  const footerContent = poweredBy ? (
    <Typography variant="caption" color="text.secondary" textAlign="center" display="block" py={2} data-testid="text-powered-by">
      {t("common.poweredByEposNow")}
    </Typography>
  ) : null;

  if (step === "confirmation" && bookingConfirmed) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }} style={themeStyle}>
        <Paper elevation={0} square sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}>
          <Box sx={{ maxWidth: 672, mx: "auto", px: 2, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            {headerContent}
            <ThemeToggle />
          </Box>
        </Paper>
        <Box sx={{ maxWidth: 448, mx: "auto", px: 2, py: 8, textAlign: "center" }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "success.light",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircle sx={{ fontSize: 32, color: "success.dark" }} />
            </Box>
          </Box>
          <Typography variant="h4" fontWeight={700} gutterBottom data-testid="text-booking-confirmed">
            {t("bookingFlow.bookingConfirmed")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            {t("bookingFlow.confirmationSent", { email: customerDetails.email })}
          </Typography>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: "left" }}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" typography="body2">
                  <Typography color="text.secondary">{t("bookingFlow.stepService")}</Typography>
                  <Typography fontWeight={500}>{selectedService?.name}</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" typography="body2">
                  <Typography color="text.secondary">{t("bookingFlow.stepStaff")}</Typography>
                  <Typography fontWeight={500}>{selectedStaff?.displayName || t("bookingFlow.anyStaff")}</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" typography="body2">
                  <Typography color="text.secondary">{t("common.date")}</Typography>
                  <Typography fontWeight={500}>{formatDateOnly(selectedDate)}</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" typography="body2">
                  <Typography color="text.secondary">{t("common.time")}</Typography>
                  <Typography fontWeight={500}>{selectedTime}</Typography>
                </Stack>
                {showPrices && (
                  <>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between" typography="body2">
                      <Typography color="text.secondary">{t("common.price")}</Typography>
                      <Typography fontWeight={500}>
                        {formatCents(selectedService?.priceCents || 0, business?.currency || "GBP")}
                      </Typography>
                    </Stack>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
          <Button
            variant="outlined"
            sx={{ mt: 3 }}
            onClick={() => {
              setStep("services");
              setSelectedService(null);
              setSelectedStaff(null);
              setSelectedDate("");
              setSelectedTime("");
              setCustomerDetails({ firstName: "", lastName: "", email: "", phone: "" });
              setBookingConfirmed(null);
            }}
            data-testid="button-book-another"
          >
            {t("bookingFlow.bookAnother")}
          </Button>
        </Box>
        {footerContent}
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }} style={themeStyle}>
      {coverImageUrl && step === "services" && (
        <Box
          sx={{
            height: { xs: 160, sm: 208 },
            backgroundImage: `url(${coverImageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
          }}
          data-testid="booking-cover-image"
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6))",
            }}
          />
          <Box sx={{ position: "absolute", bottom: 16, left: 16, right: 16, maxWidth: 672, mx: "auto" }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {logoUrl && (
                <Box
                  component="img"
                  src={logoUrl}
                  alt={String(t("bookingFlow.logoAlt"))}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1,
                    objectFit: "cover",
                    border: "2px solid rgba(255,255,255,0.2)",
                  }}
                  data-testid="booking-cover-logo"
                />
              )}
              <Box>
                <Typography variant="h5" fontWeight={700} color="common.white" data-testid="booking-cover-title">
                  {bookingPageHeading}
                </Typography>
                {tagline && (
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }} data-testid="booking-cover-tagline">
                    {tagline}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
        </Box>
      )}

      <Paper elevation={0} square sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper", position: "sticky", top: 0, zIndex: 50 }}>
        <Box sx={{ maxWidth: 672, mx: "auto", px: 2, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {step !== "services" && (
              <IconButton onClick={goBack} size="small" data-testid="button-booking-back" aria-label={t("bookingFlow.ariaBack")}>
                <ArrowBack />
              </IconButton>
            )}
            {(!coverImageUrl || step !== "services") && headerContent}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {slug && (
              <Button
                component={Link}
                to={`/book/${encodeURIComponent(slug)}/waitlist`}
                size="small"
                variant="text"
                sx={{ whiteSpace: "nowrap" }}
                data-testid="link-nav-waitlist"
              >
                {t("publicWaitlist.navShort")}
              </Button>
            )}
            {cfg?.themeMode === "auto" && <ThemeToggle />}
          </Stack>
        </Box>
      </Paper>

      {isPreview && (
        <Box
          sx={{
            bgcolor: "warning.light",
            color: "warning.dark",
            textAlign: "center",
            py: 1,
            typography: "body2",
            fontWeight: 600,
          }}
          data-testid="preview-banner"
        >
          {t("bookingFlow.previewBanner")}
        </Box>
      )}

      <Box sx={{ maxWidth: 672, mx: "auto", px: 2, py: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ py: 1.5, overflowX: "auto" }}>
          {visibleSteps.map((s, i) => {
            const stepIndex = visibleSteps.indexOf(step);
            const thisIndex = i;
            const isActive = thisIndex === stepIndex;
            const isComplete = thisIndex < stepIndex;
            return (
              <Stack key={s} direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                {i > 0 && <ChevronRight sx={{ fontSize: 12, color: "text.secondary", mx: 0.5 }} />}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    typography: "caption",
                    fontWeight: 600,
                    bgcolor: isActive ? "action.selected" : "transparent",
                    color: isActive ? "primary.main" : isComplete ? "text.secondary" : "text.disabled",
                  }}
                >
                  {isComplete && <Check sx={{ fontSize: 12 }} />}
                  <span>{visibleLabels[i]}</span>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Box>

      <Box component="main" sx={{ maxWidth: 672, mx: "auto", px: 2, pb: 4 }}>
        {step === "services" && (
          <Stack spacing={3}>
            {(business?.description ||
              (showAddress && business?.address) ||
              (showPhone && business?.phone)) && (
              <Box>
                {business?.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {business.description}
                  </Typography>
                )}
                <Stack direction="row" flexWrap="wrap" gap={2} typography="body2" color="text.secondary">
                  {showAddress && business?.address && (
                    <Stack direction="row" alignItems="center" gap={0.5} data-testid="booking-address">
                      <Map sx={{ fontSize: 16 }} />
                      {business.address}
                    </Stack>
                  )}
                  {showPhone && business?.phone && (
                    <Stack direction="row" alignItems="center" gap={0.5} data-testid="booking-phone">
                      <Phone sx={{ fontSize: 16 }} />
                      {business.phone}
                    </Stack>
                  )}
                </Stack>
              </Box>
            )}

            {galleryUrls.length > 0 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 1,
                }}
                data-testid="booking-gallery"
              >
                {galleryUrls.slice(0, 6).map((url, i) => (
                  <Box
                    key={i}
                    component="img"
                    src={url}
                    alt={String(t("bookingFlow.galleryAlt", { n: i + 1 }))}
                    sx={{ width: "100%", height: { xs: 96, sm: 128 }, objectFit: "cover", borderRadius: 1 }}
                  />
                ))}
              </Box>
            )}

            {!loadingServices && !servicesError && activeServices.length > 0 && (
              <Typography variant="subtitle1" fontWeight={600} component="h2">
                {t("bookingFlow.availableOfferings")}
              </Typography>
            )}

            {loadingServices ? (
              <Stack spacing={1.5}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rounded" height={96} />
                ))}
              </Stack>
            ) : servicesError ? (
              <Box
                sx={{
                  py: 6,
                  px: 2,
                  textAlign: "center",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
                data-testid="booking-services-error"
              >
                <Typography variant="body1" color="error" sx={{ mb: 2 }}>
                  {t("bookingFlow.offeringsLoadFailed")}
                </Typography>
                <Button variant="outlined" onClick={() => void refetchServices()} data-testid="button-retry-services">
                  {t("actions.retry")}
                </Button>
              </Box>
            ) : activeServices.length === 0 ? (
              <Box
                sx={{
                  py: 6,
                  px: 2,
                  textAlign: "center",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
                data-testid="booking-no-offerings"
              >
                <Typography variant="body1" color="text.secondary">
                  {t("bookingFlow.noOfferingsAvailable")}
                </Typography>
              </Box>
            ) : (
              Object.entries(grouped).map(([category, categoryServices]) => (
                <Stack key={category} spacing={1.5}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                    {category}
                  </Typography>
                  {categoryServices.map((service) => (
                    <Card
                      key={service.id}
                      variant="outlined"
                      sx={{
                        cursor: "pointer",
                        transition: (t) => t.transitions.create("box-shadow"),
                        "&:hover": { boxShadow: 2 },
                      }}
                      onClick={() => selectService(service)}
                      data-testid={`card-service-${service.id}`}
                    >
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography fontWeight={500}>{service.name}</Typography>
                            {service.description && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  mt: 0.5,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {service.description}
                              </Typography>
                            )}
                            {showDurations && (
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                                <Stack direction="row" alignItems="center" spacing={0.5} typography="body2" color="text.secondary">
                                  <AccessTime sx={{ fontSize: 16 }} />
                                  {formatDuration(service.durationMins)}
                                </Stack>
                              </Stack>
                            )}
                          </Box>
                          <Stack alignItems="flex-end" sx={{ flexShrink: 0 }}>
                            {showPrices && (
                              <Typography fontWeight={600}>{formatCents(service.priceCents, business?.currency || "GBP")}</Typography>
                            )}
                            <ChevronRight sx={{ fontSize: 20, color: "text.secondary", mt: 1 }} />
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ))
            )}
          </Stack>
        )}

        {step === "staff" && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {t("bookingFlow.chooseStylist")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("bookingFlow.forServiceOnly", { name: selectedService?.name ?? "" })}
              </Typography>
            </Box>

            <Card
              variant="outlined"
              sx={{ cursor: "pointer", "&:hover": { boxShadow: 2 } }}
              onClick={() => selectStaff(null)}
              data-testid="card-staff-any"
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ width: 40, height: 40 }}>
                    <Person sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={500}>{t("bookingFlow.anyAvailableTitle")}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("bookingFlow.anyAvailableSubtitle")}
                    </Typography>
                  </Box>
                  <ChevronRight sx={{ color: "text.secondary" }} />
                </Stack>
              </CardContent>
            </Card>

            {eligibleStaff
              ?.filter((s) => s.active)
              .map((member) => (
                <Card
                  key={member.id}
                  variant="outlined"
                  sx={{ cursor: "pointer", "&:hover": { boxShadow: 2 } }}
                  onClick={() => selectStaff(member)}
                  data-testid={`card-staff-${member.id}`}
                >
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar sx={{ width: 40, height: 40 }}>{getInitials(member.displayName)}</Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={500}>{member.displayName}</Typography>
                        {member.bio && (
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {member.bio}
                          </Typography>
                        )}
                      </Box>
                      <ChevronRight sx={{ color: "text.secondary" }} />
                    </Stack>
                  </CardContent>
                </Card>
              ))}
          </Stack>
        )}

        {step === "datetime" && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {t("bookingFlow.pickDateTime")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedService?.name}
                {selectedStaff ? t("bookingFlow.withStaffLine", { name: selectedStaff.displayName }) : ""}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1.5 }}>
                {t("bookingFlow.selectDateLabel")}
              </Typography>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <IconButton
                  aria-label={t("bookingFlow.calendarPrevMonth")}
                  onClick={() => setCalendarMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
                  disabled={!canGoPrevMonth}
                  size="small"
                  data-testid="button-calendar-prev-month"
                >
                  <ChevronLeft />
                </IconButton>
                <Typography variant="subtitle1" fontWeight={600} component="p" sx={{ m: 0 }} data-testid="text-calendar-month">
                  {calendarMonthLabel}
                </Typography>
                <IconButton
                  aria-label={t("bookingFlow.calendarNextMonth")}
                  onClick={() => setCalendarMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
                  disabled={!canGoNextMonth}
                  size="small"
                  data-testid="button-calendar-next-month"
                >
                  <ChevronRight />
                </IconButton>
              </Stack>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                  gap: 0.5,
                  mb: 0.75,
                }}
              >
                {WEEKDAY_ORDER_MON_FIRST.map((dayIdx) => (
                  <Typography
                    key={dayIdx}
                    variant="caption"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ fontWeight: 600, fontSize: 11 }}
                  >
                    {t(`daysOfWeekShort.${dayIdx}`)}
                  </Typography>
                ))}
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                  gap: 0.5,
                  rowGap: 1,
                }}
              >
                {monthCalendarCells.map((iso, idx) => {
                  if (!iso) {
                    return <Box key={`calendar-pad-${idx}`} />;
                  }
                  const selectable = isDateSelectable(iso);
                  const selected = selectedDate === iso;
                  const isToday = iso === todayIsoStr;
                  const dayNum = Number(iso.slice(8, 10));
                  return (
                    <Box
                      key={iso}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        minHeight: 56,
                      }}
                    >
                      <Box
                        component="button"
                        type="button"
                        disabled={!selectable}
                        onClick={() => {
                          if (!selectable) return;
                          setSelectedDate(iso);
                          setSelectedTime("");
                        }}
                        sx={{
                          aspectRatio: 1,
                          width: "100%",
                          maxWidth: 44,
                          border: "none",
                          p: 0,
                          borderRadius: "50%",
                          cursor: selectable ? "pointer" : "default",
                          bgcolor: selected ? "primary.main" : "transparent",
                          color: selected ? "primary.contrastText" : selectable ? "text.primary" : "action.disabled",
                          typography: "body2",
                          fontWeight: selected ? 600 : 400,
                          transition: (th) => th.transitions.create(["background-color", "color"], { duration: 150 }),
                          "&:hover": selectable
                            ? { bgcolor: selected ? "primary.dark" : "action.hover" }
                            : undefined,
                          "&:disabled": {
                            opacity: 1,
                            color: "action.disabled",
                          },
                        }}
                        data-testid={`button-date-${iso}`}
                      >
                        {dayNum}
                      </Box>
                      {isToday && selectable && (
                        <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 600, mt: 0.25, color: "text.secondary" }}>
                          {t("bookingFlow.today")}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {selectedDate && (
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  {t("bookingFlow.availableTimes")}
                </Typography>
                {!availableSlots ? (
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(4, 1fr)" }, gap: 1 }}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <Skeleton key={i} variant="rounded" height={36} />
                    ))}
                  </Box>
                ) : availableSlots.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <CalendarMonth sx={{ fontSize: 32, color: "action.disabled", mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {t("bookingFlow.noTimesThisDate")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {t("bookingFlow.tryDifferentDate")}
                    </Typography>
                    {slug && selectedService && (
                      <Button
                        component={Link}
                        to={`/book/${encodeURIComponent(slug)}/waitlist`}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 2 }}
                        data-testid="link-waitlist-from-no-slots"
                      >
                        {t("publicWaitlist.ctaFromCalendar")}
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(4, 1fr)" }, gap: 1 }}>
                    {availableSlots.map((slot) => (
                      <Button
                        key={slot.time}
                        variant={selectedTime === slot.time ? "contained" : "outlined"}
                        size="small"
                        onClick={() => selectSlot(slot)}
                        data-testid={`button-time-${slot.time}`}
                      >
                        {slot.display}
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        )}

        {step === "details" && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {t("bookingFlow.yourDetailsTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("bookingFlow.yourDetailsSubtitle")}
              </Typography>
            </Box>

            <Card variant="outlined">
              <CardContent>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" typography="body2">
                    <Typography color="text.secondary">{t("bookingFlow.stepService")}</Typography>
                    <Typography fontWeight={500}>{selectedService?.name}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" typography="body2">
                    <Typography color="text.secondary">{t("bookingFlow.stepStaff")}</Typography>
                    <Typography fontWeight={500}>{selectedStaff?.displayName || t("bookingFlow.anyStaff")}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" typography="body2">
                    <Typography color="text.secondary">{t("common.date")}</Typography>
                    <Typography fontWeight={500}>
                      {new Date(`${selectedDate  }T12:00:00`).toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      })}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" typography="body2">
                    <Typography color="text.secondary">{t("common.time")}</Typography>
                    <Typography fontWeight={500}>{selectedTime}</Typography>
                  </Stack>
                  <Divider />
                  {showPrices && (
                    <Stack direction="row" justifyContent="space-between" typography="body2">
                      <Typography color="text.secondary">{t("bookingFlow.priceLabel")}</Typography>
                      <Typography fontWeight={500}>
                        {formatCents(selectedService?.priceCents || 0, depositInfo?.currency || "GBP")}
                      </Typography>
                    </Stack>
                  )}
                  {showPrices && depositInfo?.enabled && depositInfo.depositAmountCents > 0 && (
                    <>
                      <Stack direction="row" justifyContent="space-between" typography="body2" alignItems="baseline">
                        <Typography color="text.secondary">{t("bookingFlow.depositDueToday")}</Typography>
                        <Typography fontWeight={600} color="primary.main">
                          {formatCents(depositInfo.depositAmountCents, depositInfo.currency)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" typography="body2">
                        <Typography color="text.secondary">{t("bookingFlow.dueAtAppointment")}</Typography>
                        <Typography fontWeight={500}>
                          {formatCents(
                            (selectedService?.priceCents || 0) - depositInfo.depositAmountCents,
                            depositInfo.currency,
                          )}
                        </Typography>
                      </Stack>
                    </>
                  )}
                  {(!depositInfo?.enabled || depositInfo.depositAmountCents === 0) && (
                    <Stack direction="row" justifyContent="space-between" typography="body2">
                      <Typography color="text.secondary">{t("bookingFlow.payment")}</Typography>
                      <Typography color="text.secondary">{t("bookingFlow.payAtAppointment")}</Typography>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={(theme) => ({
                bgcolor: theme.palette.mode === "light" ? "#ffffff" : theme.palette.background.paper,
                borderColor: "divider",
              })}
            >
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      required
                      label={t("bookingFlow.firstNameLabel")}
                      value={customerDetails.firstName}
                      onChange={(e) => {
                        setCustomerDetails({ ...customerDetails, firstName: e.target.value });
                        if (detailsFieldErrors.firstName) setDetailsFieldErrors((prev) => ({ ...prev, firstName: undefined }));
                      }}
                      placeholder={t("bookingFlow.placeholderFirstName")}
                      data-testid="input-booking-firstname"
                      fullWidth
                      error={Boolean(detailsFieldErrors.firstName)}
                      helperText={detailsFieldErrors.firstName}
                    />
                    <TextField
                      required
                      label={t("bookingFlow.lastNameLabel")}
                      value={customerDetails.lastName}
                      onChange={(e) => {
                        setCustomerDetails({ ...customerDetails, lastName: e.target.value });
                        if (detailsFieldErrors.lastName) setDetailsFieldErrors((prev) => ({ ...prev, lastName: undefined }));
                      }}
                      placeholder={t("bookingFlow.placeholderLastName")}
                      data-testid="input-booking-lastname"
                      fullWidth
                      error={Boolean(detailsFieldErrors.lastName)}
                      helperText={detailsFieldErrors.lastName}
                    />
                  </Stack>
                  <TextField
                    required
                    label={t("bookingFlow.emailLabel")}
                    type="email"
                    value={customerDetails.email}
                    onChange={(e) => {
                      setCustomerDetails({ ...customerDetails, email: e.target.value });
                      if (detailsFieldErrors.email) setDetailsFieldErrors((prev) => ({ ...prev, email: undefined }));
                    }}
                    placeholder={t("bookingFlow.placeholderEmail")}
                    data-testid="input-booking-email"
                    fullWidth
                    error={Boolean(detailsFieldErrors.email)}
                    helperText={detailsFieldErrors.email}
                  />
                  <TextField
                    label={t("bookingFlow.phoneLabel")}
                    value={customerDetails.phone}
                    onChange={(e) => setCustomerDetails({ ...customerDetails, phone: e.target.value })}
                    placeholder={t("bookingFlow.placeholderPhone")}
                    data-testid="input-booking-phone"
                    fullWidth
                  />

                  <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
                    {business?.cancellationPolicyText?.trim()
                      ? business.cancellationPolicyText
                      : t("bookingFlow.defaultCancellationPolicy")}
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={submitBooking}
                    disabled={bookingMutation.isPending}
                    data-testid="button-confirm-booking"
                  >
                    {bookingMutation.isPending
                      ? t("bookingFlow.processing")
                      : depositInfo?.enabled && depositInfo.depositAmountCents > 0
                        ? t("bookingFlow.payDeposit", {
                            amount: formatCents(depositInfo.depositAmountCents, depositInfo.currency),
                          })
                        : t("bookingFlow.confirmBooking")}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}
      </Box>

      {footerContent}
    </Box>
  );
}
